import { useAuth0 } from '@auth0/auth0-react';
import { Stack, useDisclosure } from '@chakra-ui/react';
import { ArrowRight, ArrowLeft } from '@phosphor-icons/react';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import EducationalDrawer from 'Containers/_drawers/EducationalDrawer';
import { handleUnlockMainNav, updateElections } from 'Containers/App/actions';
import { GO_GUEST_ID } from 'Containers/App/constants';
import {
  makeHasSelectedRecommendation,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectGlobalField,
  makeSelectJvpField,
  selectIsFirstPageView,
} from 'Containers/App/selectors';
import { AlexProducts, JVPState } from 'Containers/App/types';
import {
  getInvalidPlanMessageKey,
  getPlanFitField,
  handleBcNavigate,
  moveSelectedPlanToTop,
  sortPlansAlphabetically,
} from 'Containers/App/utils';
import {
  changeFormValue,
  getResultsPageData,
  recommendWithEphemeralPlan,
  selectHealthPlan,
  selectHealthPlanStoreOnly,
  setSelectedMemberIds,
  waiveHealthCoverage,
} from 'Containers/CommercialRoutes/actions';
import { makeGetHsaContributionLimit, makeGetPlans } from 'Containers/CommercialRoutes/selectors';
import { EnteredPlan } from 'Containers/CommercialRoutes/types';
import {
  clearSupplementalPlansCache,
  getSupplementalPlans,
  setDefaultSelectedPlans,
} from 'Containers/OverviewPage/actions';
import { makeSelectOverviewField } from 'Containers/OverviewPage/selectors';
import {
  changeSurveyValueFromResultPage,
  setActiveHouseholdMembers,
  setIncentiveSurvey,
  submitResultIncentiveSurvey,
  updateHouseholdFromResultPage,
} from 'Containers/ProfilePage/actions';
import { CoverageMessage } from 'Containers/ProfilePage/MemberSection/styled';
import {
  makeGetBuilderGoSetting,
  makeGetBuilderGoSettings,
  makeGetHasSpouse,
  makeGetHealthPlanMemberIds,
  makeGetHousehold,
  makeGetIsFamily,
  makeGetSelectedProduct,
  makeGetSelectedPublicationPlanYear,
  makeGetSelectedPublicationSegmentSlug,
  makeGetTierCodeData,
  makeSelectProfileField,
  makeSelectShowClientSurvey,
  makeSelectSpouse,
  makeSelectSurvey,
} from 'Containers/ProfilePage/selectors';
import {
  ClaimsSummary,
  IncentiveSurveyAnswer,
  IncentiveSurveyQuestion,
  Member,
  ResultPageIncentiveSurveyData,
  Survey,
} from 'Containers/ProfilePage/types';
import { ClaimsBanner } from 'Containers/ResultPage/_banner/BannerCards/ClaimsBanner';
import ClientDisclaimer from 'Containers/ResultPage/ClientDisclaimer';
import { ContentfulTextNoteType } from 'ContentfulDefaults/types/_shared';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { ContentfulHeaderWrapperForHealthSection } from 'ContentfulWrappers/header';
import { useCompareContext } from 'Contexts/compareContext';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useProfileContext } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import { FadeUp, Spinner, Icon } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { useViewport } from 'DesignLibrary/context';
import Plan from 'Models/plan';
import {
  Household,
  ProfileIncentiveQuestionAnswers,
  Recommendation as RecommendationType,
  SupplementalPlansFullHousehold,
  SAVINGS_ACCOUNT_TYPES,
  InvalidPlanMessageKey,
} from 'Types/entities';
import { AriaHiddenAlert } from 'Utils/accessibility';
import { BuilderGoSettings, Election, PeopleLikeYou } from 'Utils/apiTypes';
import { formatBuilderPlansForInsights, sendInsightsEvent } from 'Utils/insights';
import { getBCPath, OVERVIEW_PATH, RESULT_PATH } from 'Utils/urls';

import { AccountsBanner, BuildAPlanBanner, PredictionsBanner, WaiveBar } from './_banner/BannerCards';
import CompareBar from './CompareBar';
import CompareModal from './CompareModal';
import ExplainerModal from './ExplainerModal';
import InvalidPlanSelectionMessage from './InvalidPlanSelectionMessage';
import { LoadingScreen } from './LoadingScreen';
import { MemberSelectionCard } from './MemberSelectionCard';
import { MemberDivider } from './MemberSelectionCard/styled';
import NoEligiblePlans from './NoEligiblePlans';
import PlanEntryModal from './PlanEntryModal';
import { ProviderSelectionContent } from './ProviderSelectionContent';
import Recommendation from './Recommendation';
import {
  BottomCards,
  GridContainer,
  HorizontalScrollBar,
  MemberLoadingContainer,
  PlanCards,
  PlanChangeMessage,
  Sidebar,
  TableView,
  PaginationContainer,
  PaginationButton,
  PaginationIcon,
} from './styled';

export interface MatchedPlans {
  election: Election;
  matchingPlan: RecommendationType;
}

interface ResultPageStateProps {
  selectedProduct: AlexProducts | '';
  availablePlans: RecommendationType[] | [];
  maximumAvailableAnnualSihraContribution: number;
  peopleLikeYou: PeopleLikeYou | Record<string, never>;
  isFamily: boolean;
  hasSpouse: boolean;
  showClientSurvey: boolean;
  individualHsaContributionLimit: number;
  familyHsaContributionLimit: number;
  hsaContributionLimit: number;
  isDirty: boolean;
  isLoading: boolean;
  isPLYEnabled: boolean;
  killMedRec: boolean;
  selectedPlan: RecommendationType | Record<string, never>;
  isFullBenefitEnabled: boolean;
  canWaiveCoverage: boolean;
  household: Household;
  enteredPlan: EnteredPlan;
  isSpousalPlanEnabled: boolean;
  isPlanComparisonEnabled: boolean;
  supplementalPlans: SupplementalPlansFullHousehold;
  isFirstPageView: boolean;
  searchProviderLinks: Record<string, string>;
  selectedMemberIds: string[];
  survey: Survey;
  isCoverageWaived: boolean;
  jvp: JVPState;
  isDemoMode: boolean;
  planYear: number | undefined;
  validatedSegmentSlug: string | undefined;
  bcUrlHash: string;
  builderCustomerKey: string | null;
  incentiveSurvey: IncentiveSurveyQuestion[];
  isIncentiveSurveyLoading: boolean;
  incentiveSurveyAnswers: Record<string, IncentiveSurveyAnswer | null>;
  resultPageIncentiveSurvey: ResultPageIncentiveSurveyData;
  isReturnUser: boolean | null;
  elections: Election[];
  tierCode: string[];
  hasSelectedRecommendation: boolean;
  builderGoSettings: BuilderGoSettings | null;
  spouse?: Member;
  claimsSummaryId: string | null;
  claimsSummary: ClaimsSummary | null;
  claimsSummaryPolling: boolean;
  claimsError: Error | null;
}

interface ResultPageDispatchProps {
  lockFuturePages: () => void;
  getRecommendations: () => void;
  getRecommendationsWithEphemeralPlan: (plan: EnteredPlan) => void;
  saveEphemeralPlan: (plan: EnteredPlan) => void;
  waiveCoverage: () => void;
  selectHealthPlan: (plan: RecommendationType | Record<string, never>) => void;
  selectHealthPlanStoreOnly: (plan: RecommendationType | Record<string, never>) => void;
  fetchSupplementalPlans: () => void;
  setSelectedMemberIds: (memberIds: string[]) => void;
  handleSavePregnancyAnswer: (value: string) => void;
  resetSelectedSupplementalPlans: () => void;
  clearSupplementalCache: () => void;
  createUpdateHouseholdAndIncentiveSurvey: () => void;
  submitIncentiveSurveyResponses: (incentiveSurveyResonses) => void;
  updateActiveMembers: (member: string[]) => void;
  setProfileIncentiveSurvey: (data: ProfileIncentiveQuestionAnswers) => void;
  updateElections: (data: Election[] | []) => void;
}
export type ResultPageProps = ResultPageStateProps & ResultPageDispatchProps;

export const ResultPage = ({
  selectedProduct,
  availablePlans,
  maximumAvailableAnnualSihraContribution,
  peopleLikeYou,
  isFamily,
  hasSpouse,
  showClientSurvey,
  individualHsaContributionLimit,
  familyHsaContributionLimit,
  hsaContributionLimit,
  isDirty,
  isLoading,
  isPLYEnabled,
  killMedRec,
  selectedPlan,
  isFullBenefitEnabled,
  canWaiveCoverage,
  household,
  enteredPlan,
  isSpousalPlanEnabled,
  isPlanComparisonEnabled,
  supplementalPlans,
  isFirstPageView,
  searchProviderLinks,
  selectedMemberIds,
  survey,
  isCoverageWaived,
  planYear,
  validatedSegmentSlug,
  bcUrlHash,
  jvp,
  isDemoMode,
  builderCustomerKey,
  incentiveSurvey,
  isIncentiveSurveyLoading,
  incentiveSurveyAnswers,
  lockFuturePages,
  getRecommendations,
  getRecommendationsWithEphemeralPlan,
  saveEphemeralPlan,
  waiveCoverage,
  selectHealthPlan,
  selectHealthPlanStoreOnly,
  fetchSupplementalPlans,
  setSelectedMemberIds,
  handleSavePregnancyAnswer,
  resetSelectedSupplementalPlans,
  clearSupplementalCache,
  createUpdateHouseholdAndIncentiveSurvey,
  submitIncentiveSurveyResponses,
  updateActiveMembers,
  setProfileIncentiveSurvey,
  elections,
  updateElections,
  resultPageIncentiveSurvey,
  isReturnUser,
  tierCode,
  hasSelectedRecommendation,
  builderGoSettings,
  spouse,
  claimsSummaryId,
  claimsSummary,
  claimsSummaryPolling,
  claimsError,
}: ResultPageProps) => {
  const { resetComparedPlans } = useCompareContext();
  const { device } = useViewport();
  const { retrieveContentfulData } = useTextContext();
  const { handleSectionChange, selectedPublicationInfo, reviewMode, tableViewEnabled } = useProfileContext();
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const {
    is_educational_videos_enabled,
    is_profile_review_tool_enabled,
    is_no_deductible_plans_enabled,
    is_election_members_enabled,
  } = useFeatureFlagContext();
  const navigate = useNavigate();

  useEffect(() => {
    const profileIncentiveSurvey = [...incentiveSurvey];
    return () => {
      setProfileIncentiveSurvey({
        incentiveSurvey: profileIncentiveSurvey,
        incentiveSurveyAnswers,
      });
    };
  }, []);

  const isDevMode = localStorage.getItem('dev');
  // ----------------------
  // LOADING INFO
  // ----------------------
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [isShowResults, setIsShowResults] = useState(true);
  const [isMinLoadTimeElapsed, setIsMinLoadTimeElapsed] = useState(false);
  const [didMount, setDidMount] = useState(false);
  const [showPreviousSelectedPlanMessage, setShowPreviousSelectedPlanMessage] = useState(true);
  const [isDefaultPrevSelected, setIsDefaultPrevSelected] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const isReviewModeEnabled = isShowResults && reviewMode;

  const plansPerPage = 3; // Always 3 plans per page
  const totalPlans = availablePlans.length;
  const totalPages = Math.ceil(availablePlans.length / plansPerPage);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  const shouldShowPagination = tableViewEnabled && reviewMode && availablePlans.length > 3;

  const getPlansPerPage = (plans: Plan[]) => {
    if (plans.length <= 3) return plans; // Show all plans if 3 or fewer
    const startIndex = (currentPage - 1) * plansPerPage;
    return plans.slice(startIndex, startIndex + plansPerPage);
  };

  const goToNextPage = () => {
    if (currentPage < Math.ceil(totalPlans / plansPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const startPlan = (currentPage - 1) * plansPerPage + 1;
  const endPlan = Math.min(currentPage * plansPerPage, totalPlans);
  const planDisplay =
    startPlan === endPlan ? `${endPlan} of ${totalPlans}` : `${startPlan}-${endPlan} of ${totalPlans}`;
  // communicates information to screen readers when value changes
  const [hiddenAlert, setHiddenAlert] = useState<string | null>(null);

  // display provider search section feature enabled from alex builder
  const docFinderEnabled = selectedPublicationInfo?.publication?.docfinder_enabled;

  const selectedProductIsBc = selectedProduct === 'bc';

  const accessibilityContent = retrieveContentfulData<Record<string, string>>(
    'health_section.secondary_content.accessibility',
  );

  useEffect(() => {
    const shouldShowLoading = isDirty || enteredPlan.isDirty;
    if (!isShowLoading && shouldShowLoading && !isDevMode) {
      setIsShowLoading(true);
      setIsShowResults(false);
      setHiddenAlert(accessibilityContent.loading_bar_alert);
    }
  }, [isDirty, enteredPlan?.isDirty]);

  useEffect(() => {
    if (isLoading) {
      setHiddenAlert(accessibilityContent.loading_bar_alert);
      setIsMinLoadTimeElapsed(true);
      setTimeout(() => {
        setIsMinLoadTimeElapsed(false);
      }, 3000);
    } else if (isShowResults && didMount) {
      // TODO - reorganize this whole page's render order so we don't need to
      //  sidestep first tick render issues with this didMount variable
      setHiddenAlert(accessibilityContent.loading_bar_complete_alert);

      // *** HANDLE BC REDIRECT HERE ***
      if (
        (selectedProductIsBc && !isReviewModeEnabled) ||
        /* adding below check in case where user is on audio path and he selects
        review mode and if no plans are available, then navigate user to parabola. */
        (selectedProductIsBc && isReviewModeEnabled && availablePlans?.length === 0)
      ) {
        handleBcNavigate(
          jvp,
          isDemoMode,
          planYear,
          validatedSegmentSlug,
          bcUrlHash,
          GO_GUEST_ID,
          isAuthenticated,
          getIdTokenClaims,
          getBCPath,
        );
      } else if (availablePlans.length === 0) {
        navigate(OVERVIEW_PATH);
      }
    }
  }, [isLoading, isShowResults]);

  const isHouseholdLoading = !isDevMode && (isLoading || isMinLoadTimeElapsed);

  const handleOnLoadingComplete = () => {
    const SHOW_RESULTS = 1000; // Slight delay before showing results

    setIsShowLoading(false);

    setTimeout(() => {
      setIsShowResults(true);
      setHiddenAlert(accessibilityContent.loading_bar_complete_alert);
    }, SHOW_RESULTS);
  };

  // ----------------------
  // MODAL WINDOW STATE
  // ----------------------
  const { isOpen: hsaOpen, onOpen: hsaOnOpen, onClose: hsaOnClose } = useDisclosure();
  const { isOpen: hraOpen, onOpen: hraOnOpen, onClose: hraOnClose } = useDisclosure();
  const { isOpen: fsaOpen, onOpen: fsaOnOpen, onClose: fsaOnClose } = useDisclosure();
  const { isOpen: predictionsOpen, onOpen: predictionsOnOpen, onClose: predictionsOnClose } = useDisclosure();
  const { isOpen: planEntryOpen, onOpen: planEntryOnOpen, onClose: planEntryOnClose } = useDisclosure();
  const { isOpen: compareOpen, onOpen: compareOnOpen, onClose: compareOnClose } = useDisclosure();

  useEffect(() => {
    if (isDirty) {
      resetComparedPlans(); // reset compared plans if profile is dirty
      getRecommendations(); // only request recommendations if profile is dirty
      resetSelectedSupplementalPlans(); // reset selected fbs plans if profile is dirty
      clearSupplementalCache(); // reset supplemental plans cache if profile is dirty
    }

    sendInsightsEvent(null, 'result_page');
    setDidMount(true);
    // calling function to set unlockedMainPath state as RESULT_PATH when we naviage to result page
    lockFuturePages();
  }, []);

  useEffect(() => {
    if (availablePlans.length > 0 && !isDirty) {
      // Need to fetch supplemental plans to determine whether or not FSA info modal button should display
      //   -Trigger in this useEffect to prevent calling fetchSupplemental before getRecommendations
      fetchSupplementalPlans();

      const builderPlans = formatBuilderPlansForInsights(availablePlans);

      const updatedBuilderPlans = builderPlans?.map((plan) => ({
        ...plan,
        tier: retrieveContentfulData<string>(getPlanFitField(plan?.score)), // retrive original text from contentful and pass to insights event as tier
      }));

      sendInsightsEvent(null, 'recommended_plan', {
        recommendations: updatedBuilderPlans,
      });
    }
  }, [isDirty]);

  // Remove when retiring feature flag APP-1028-oop-estimate-uses-comm-api, APP-806
  const { is_real_cost_before_tax_benefit_enabled } = useFeatureFlagContext();

  // TODO: move this to a selector
  function getEmployerHSAContributionMax() {
    if (hasSelectedRecommendation && selectedPlan.plan.hsa_eligible) {
      const plan = new Plan(selectedPlan as RecommendationType);
      return plan.calcEmployerHsaContribution(hsaContributionLimit);
    }
    return Math.max(
      ...availablePlans.map((plan) => new Plan(plan).calcEmployerHsaContribution(hsaContributionLimit) || 0),
    );
  }

  const employerHSAContributionMax = getEmployerHSAContributionMax();

  // TODO: move this to a selector
  function getEmployerHRAContributionMax() {
    if (hasSelectedRecommendation && selectedPlan.plan.hra_eligible) {
      const plan = new Plan(selectedPlan as RecommendationType);
      return plan.employerHraContribution;
    }
    return Math.max(...availablePlans.map((plan) => new Plan(plan).employerHraContribution || 0));
  }

  const employerHRAContributionMax = getEmployerHRAContributionMax();

  const handleSelectHealthPlan = (recommendation) => {
    // Create a shallow copy of the recommendation object
    const updatedRecommendation = { ...recommendation };
    updatedRecommendation.publication = survey.enrollment_question.split('-')[0];
    // set selected plan on Commercial reducer + post enrollment data
    selectHealthPlan(updatedRecommendation);
    setHiddenAlert(accessibilityContent.plan_selected_alert);
  };

  const handleEphemeralPlanRecommendation = (plan) => {
    resetComparedPlans();
    getRecommendationsWithEphemeralPlan(plan);
  };

  const handleDeselect = () => {
    updateElections([]);
    // set deselected plan on Commercial reducer + post enrollment data
    setIsDefaultPrevSelected(false);
    selectHealthPlan({});
    lockFuturePages();
  };

  const handleWaiveHealthPlan = () => {
    setIsDefaultPrevSelected(false);
    updateElections([
      {
        planable_type: 'MedicalPlan',
        waived: true,
      },
    ]);
    selectHealthPlan({});
    waiveCoverage();
    setHiddenAlert(accessibilityContent.waived_coverage_alert);
  };

  // TODO: TICKETED Add insights events to individual modal windows
  // const handleOpenInfoModal = (infoType) => {
  //   sendInsightsEvent(null, 'info_modal_view_started', { modal_id: infoType });
  // };

  // const handleCloseInfoModal = (infoType) => {
  //   sendInsightsEvent(null, 'info_modal_view_ended', { modal_id: infoType });
  // };

  let hasEnteredCustomPlan = false;
  availablePlans.forEach(({ plan }) => {
    if (plan.plan_type === 'Custom') hasEnteredCustomPlan = true;
  });

  // ----------------------
  // SECONDARY CONTENT
  // ----------------------

  const hasHsaPlan = availablePlans.some((rec) => rec.plan.hsa_eligible);
  const hasHraPlan = availablePlans.some((rec) => rec.plan.hra_eligible);
  const isFsaEligible =
    supplementalPlans?.fsa_medical?.plans?.length ||
    supplementalPlans?.fsa_dependent?.plans?.length ||
    supplementalPlans?.fsa_adoption?.plans?.length;

  const accountsBannerCard = !isReviewModeEnabled && (
    <AccountsBanner
      hasHsaPlan={hasHsaPlan}
      hasHraPlan={hasHraPlan}
      isFsaEligible={isFsaEligible}
      handleHsa={hsaOnOpen}
      handleHra={hraOnOpen}
      handleFsa={fsaOnOpen}
    />
  );

  const shouldShowClaimsBanner = !!claimsSummaryId;
  const claimsBannerCard = (
    <ClaimsBanner
      handleOpen={predictionsOnOpen}
      claimsSummary={claimsSummary}
      claimsError={claimsError}
      claimsSummaryPolling={claimsSummaryPolling}
    />
  );

  const predictionsBannerCard = isPLYEnabled && !killMedRec && !isReviewModeEnabled && (
    <PredictionsBanner handleOpen={predictionsOnOpen} key="predictions" />
  );

  const predictionsOrClaimsCard = shouldShowClaimsBanner ? claimsBannerCard : predictionsBannerCard;

  const buildPlanBannerCard = isSpousalPlanEnabled && hasSpouse && !isReviewModeEnabled && (
    <BuildAPlanBanner handleOpen={planEntryOnOpen} key="buildaplan" />
  );

  const providersSearchBannerCard = docFinderEnabled && !isReviewModeEnabled && <ProviderSelectionContent />;

  const renderWaiveCoverage = canWaiveCoverage && isFullBenefitEnabled && (
    <WaiveBar handleWaiveHealthPlan={handleWaiveHealthPlan} isCoverageWaived={isCoverageWaived} key="waive" />
  );

  const renderMemberSelectionCard = household.members.length > 1 && (
    <MemberSelectionCard
      allHouseholdMembers={household.members}
      selectedMemberIds={selectedMemberIds}
      hasEnteredCustomPlan={hasEnteredCustomPlan}
      isBuilderClient={Boolean(builderCustomerKey)}
      isLoading={isHouseholdLoading}
      survey={survey}
      updateActiveMembers={updateActiveMembers}
      incentiveSurvey={incentiveSurvey}
      isIncentiveSurveyLoading={isIncentiveSurveyLoading}
      incentiveSurveyAnswers={incentiveSurveyAnswers}
      createUpdateHouseholdAndIncentiveSurvey={createUpdateHouseholdAndIncentiveSurvey}
      submitIncentiveSurveyResponses={submitIncentiveSurveyResponses}
      handleSectionChange={(key, isValid, isComplete) => handleSectionChange({ key, isValid, isComplete })}
      setSelectedMemberIds={setSelectedMemberIds}
      lockFuturePages={lockFuturePages}
      handleSavePregnancyAnswer={handleSavePregnancyAnswer}
      key="memberSelection"
      setProfileIncentiveSurvey={setProfileIncentiveSurvey}
      resultPageIncentiveSurvey={resultPageIncentiveSurvey}
      spouse={spouse}
    />
  );

  // ----------------------
  // BUILDING RECOMMENDATIONS
  // ----------------------

  const showPrevSelectedPlan = isDefaultPrevSelected && isReturnUser && elections?.length > 0;

  const invalidPlanMessageKey = useRef<InvalidPlanMessageKey | null>(null);
  const matchedPlans: MatchedPlans | null = elections?.reduce(
    (selectedData: MatchedPlans | null, election: Election) => {
      if (selectedData) return selectedData;
      if (election.planable_type === 'MedicalPlan') {
        const matchingPlan = availablePlans?.find(
          (availablePlan) => election?.plan_details?.jv_medical_plan_id === availablePlan?.plan?.external_id,
        );
        if (matchingPlan) {
          return { election, matchingPlan };
        }
      }
      return selectedData;
    },
    null,
  );

  const effectRunRef = useRef(false);
  const selectPlanEmptyRef = useRef(false);

  useEffect(() => {
    // Don't fuss with selected plans if we're redirecting to BC
    // in order to allow BC to show plan selection invalidated messages
    if (selectedProductIsBc) {
      return;
    }

    if (availablePlans?.length > 0 && Object.keys(selectedPlan).length > 0) {
      const isPlanAvailable = availablePlans?.some(
        (availablePlan) => selectedPlan?.plan?.external_id === availablePlan?.plan?.external_id,
      );

      if (!isPlanAvailable && !selectPlanEmptyRef.current) {
        selectHealthPlanStoreOnly({});
        selectPlanEmptyRef.current = true;
      }
    }
    if (matchedPlans?.election && !effectRunRef.current) {
      if (matchedPlans?.matchingPlan && !hasSelectedRecommendation) {
        if (!household?.external_id?.startsWith('guest__')) {
          // Handle select health plan in order to ensure election members reflect latest recommendation
          handleSelectHealthPlan(matchedPlans?.matchingPlan);
        }
        effectRunRef.current = true;
      }
    } else if (
      elections?.length > 0 &&
      elections[0].planable_type === 'MedicalPlan' &&
      elections[0].waived &&
      !hasSelectedRecommendation &&
      !effectRunRef.current
    ) {
      handleWaiveHealthPlan();
      effectRunRef.current = true; // Mark the effect as run
    }

    if (isReturnUser) {
      // Invalidate message
      if (showPreviousSelectedPlanMessage && !isEmpty(availablePlans)) {
        invalidPlanMessageKey.current = getInvalidPlanMessageKey(
          tierCode,
          !isEmpty(elections),
          household.members,
          is_election_members_enabled,
          matchedPlans?.election,
        );
        if (
          invalidPlanMessageKey.current === InvalidPlanMessageKey.NoLongerEligible &&
          !selectPlanEmptyRef.current
        ) {
          selectHealthPlanStoreOnly({});
          selectPlanEmptyRef.current = true; // Mark the effect as run
        }
      }

      // show/hide previous selected plan
      const matchedWithSelectedPlan = elections?.some(
        (plan) =>
          plan.planable_type === 'MedicalPlan' &&
          plan?.plan_details?.jv_medical_plan_id === selectedPlan?.plan?.external_id,
      );

      if (
        !isDefaultPrevSelected ||
        (isEmpty(elections) && !isEmpty(availablePlans)) ||
        (hasSelectedRecommendation && !matchedWithSelectedPlan)
      ) {
        invalidPlanMessageKey.current = null;
      }

      if (isLoading) {
        setShowPreviousSelectedPlanMessage(true);
      }
      if (!isLoading && availablePlans?.length > 0) {
        if (
          !matchedPlans?.matchingPlan ||
          !elections ||
          !isDefaultPrevSelected ||
          (hasSelectedRecommendation && !matchedWithSelectedPlan)
        ) {
          setShowPreviousSelectedPlanMessage(false);
        }
      }
    } else {
      setShowPreviousSelectedPlanMessage(false);
      if (availablePlans?.length > 0) {
        const isPlanSelected = availablePlans?.some(
          (availablePlan) => selectedPlan?.plan?.external_id === availablePlan?.plan?.external_id,
        );
        if (!isPlanSelected && !selectPlanEmptyRef.current) {
          selectHealthPlanStoreOnly({});
          selectPlanEmptyRef.current = true; // Mark the effect as run
        }
      }
    }
  }, [matchedPlans?.election, availablePlans, handleSelectHealthPlan, isDefaultPrevSelected]);

  const renderRecommendations = () => {
    // Will check if showPrevSelectedPlan is true then will call moveSelectedPlanToTop function to re-arrange the availablePlans array and move selected plan array to index 0, else will keep availablePlans as it is!
    const sortedAvailablePlan = showPrevSelectedPlan
      ? moveSelectedPlanToTop(availablePlans, matchedPlans)
      : availablePlans;

    const sortedAlphabetically = sortPlansAlphabetically(availablePlans);
    if (!is_profile_review_tool_enabled || !isReviewModeEnabled) {
      return renderNormalMode(sortedAvailablePlan);
    }
    return tableViewEnabled ? renderTableView(sortedAlphabetically) : renderCardView(sortedAlphabetically);
  };

  const renderNormalMode = (plans: RecommendationType[]) => {
    const recommendations = plans?.map((rec: RecommendationType, idx: number) => {
      // useLegacyCostCalc is true when is_real_cost_before_tax_benefit_enabled is false. Remove arg when retiring feature flag APP-1028-oop-estimate-uses-comm-api
      const plan = new Plan(rec, !is_real_cost_before_tax_benefit_enabled);

      const isRecPreviouslySelected = showPrevSelectedPlan
        ? rec.plan.external_id === matchedPlans?.election?.plan_details?.jv_medical_plan_id
        : false;

      const isNoDeductibleCallOutAvailable = plan?.isNoDeductiblePlan && is_no_deductible_plans_enabled;

      return (
        <Recommendation
          dataTestId={rec.plan.external_id}
          idx={idx}
          key={rec.plan.external_id}
          isEntered={rec.plan.carrier_name === 'Custom'}
          plan={plan}
          score={plan.picwellScore}
          hsaContributionLimit={hsaContributionLimit}
          maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution}
          choosePlan={() => handleSelectHealthPlan(rec)}
          deselectPlan={handleDeselect}
          isSelected={
            hasSelectedRecommendation
              ? selectedPlan.plan.external_id === rec.plan.external_id
              : isRecPreviouslySelected
          }
          killMedRec={killMedRec}
          isPlanComparisonEnabled={isPlanComparisonEnabled}
          handleOpenPlanEntry={planEntryOnOpen}
          searchProviderLinks={searchProviderLinks}
          plyData={peopleLikeYou}
          isReviewModeEnabled={isReviewModeEnabled}
          isNoDeductibleCallOutAvailable={isNoDeductibleCallOutAvailable}
        />
      );
    });

    // Splice secondary content into recommendations list
    if (device === 'mobile') {
      if (recommendations.length > 4) {
        recommendations.splice(4, 0, buildPlanBannerCard as JSX.Element);
        recommendations.splice(3, 0, predictionsOrClaimsCard as JSX.Element);
        recommendations.splice(2, 0, accountsBannerCard as JSX.Element);
        recommendations.splice(1, 0, providersSearchBannerCard as JSX.Element);
      }
      recommendations.splice(0, 0, renderMemberSelectionCard as JSX.Element);
    }

    if (device === 'tablet') {
      recommendations.splice(
        1,
        0,
        <HorizontalScrollBar>
          {renderMemberSelectionCard}
          {providersSearchBannerCard}
          {predictionsOrClaimsCard}
          {accountsBannerCard}
          {buildPlanBannerCard}
        </HorizontalScrollBar>,
      );
    }

    return recommendations;
  };

  const renderTableView = (plans: RecommendationType[]) => {
    const comparablePlans = plans?.map((rec: RecommendationType) => {
      return new Plan(rec, !is_real_cost_before_tax_benefit_enabled);
    });
    // Apply pagination
    const paginatedPlans = getPlansPerPage(comparablePlans);

    return (
      <TableView>
        <CompareModal
          handleClose={() => {}}
          isOpen
          selectedPlan={
            hasSelectedRecommendation
              ? new Plan(
                  selectedPlan as RecommendationType,
                  !is_real_cost_before_tax_benefit_enabled, // Remove arg when retiring feature flag APP-1028-oop-estimate-uses-comm-api
                )
              : {}
          }
          choosePlan={handleSelectHealthPlan}
          deselectPlan={handleDeselect}
          hsaContributionLimit={hsaContributionLimit}
          maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution}
          killMedRec={killMedRec}
          showCompareRows={false}
          reviewModePlans={paginatedPlans}
        />
      </TableView>
    );
  };

  const renderCardView = (plans: RecommendationType[]) => renderNormalMode(plans);

  const renderResults = () => {
    const allTextNotes = retrieveContentfulData<ContentfulTextNoteType[]>('health_section.text_notes') || [];
    const sortText = retrieveContentfulData<string>('health_section.page_header.sortedtext_review_tool');
    const planYearSpecific = allTextNotes.filter(
      (note) => note.plan_year === selectedPublicationInfo?.publication.plan_year.toString(),
    );

    let contentfulTextNote: JSX.Element;
    if (planYearSpecific.length) {
      const note = planYearSpecific[0];
      contentfulTextNote = <ContentfulTextNote note={note} />;
    } else {
      // Render legacy singular text note
      contentfulTextNote = <ContentfulTextNote sectionKey="health_section" />;
    }

    return (
      <div>
        <FadeUp isOpen={isShowResults && !isHouseholdLoading}>
          <Stack gap={4}>
            <PlanChangeMessage>
              {invalidPlanMessageKey.current && (
                <InvalidPlanSelectionMessage invalidPlanMessageKey={invalidPlanMessageKey.current} />
              )}
            </PlanChangeMessage>
            {contentfulTextNote}
            <PlanCards>
              {isReviewModeEnabled && (
                <CoverageMessage data-testid="sort-message-box">
                  <Icon
                    type="SortAscending"
                    color="--colors-gray-600"
                    transform={tableViewEnabled ? 'rotate(270deg)' : undefined}
                  />
                  {sortText}

                  {shouldShowPagination && (
                    <PaginationContainer>
                      <PaginationButton
                        type="button"
                        data-testid="pagination-prev"
                        disabled={!hasPrevPage}
                        onClick={hasPrevPage ? goToPreviousPage : undefined}
                      >
                        <PaginationIcon>
                          <ArrowLeft />
                        </PaginationIcon>
                      </PaginationButton>

                      {planDisplay}

                      <PaginationButton
                        type="button"
                        data-testid="pagination-next"
                        disabled={!hasNextPage}
                        onClick={hasNextPage ? goToNextPage : undefined}
                      >
                        <PaginationIcon>
                          <ArrowRight />
                        </PaginationIcon>
                      </PaginationButton>
                    </PaginationContainer>
                  )}
                </CoverageMessage>
              )}
              {renderRecommendations()}
              {renderWaiveCoverage}
            </PlanCards>
          </Stack>
        </FadeUp>
        <FadeUp isOpen={isShowResults && isHouseholdLoading}>
          <MemberLoadingContainer>
            <Spinner size="large" color="--border-gray" />
          </MemberLoadingContainer>
        </FadeUp>
      </div>
    );
  };

  const renderResultsInGrid = () => {
    return (
      <GridContainer>
        {renderResults()}
        {device === 'desktop' && (
          <FadeUp isOpen={isShowResults}>
            <Sidebar>
              {renderMemberSelectionCard}
              {providersSearchBannerCard}
              {docFinderEnabled && <MemberDivider className="bottom-border" />}
              {predictionsOrClaimsCard}
              {accountsBannerCard}
              {buildPlanBannerCard}
            </Sidebar>
          </FadeUp>
        )}
      </GridContainer>
    );
  };

  const renderPageContent = () => {
    if (selectedProductIsBc && !isReviewModeEnabled) return null;

    if (showClientSurvey && availablePlans.length === 0) {
      return (
        <FadeUp isOpen={isShowResults}>
          <ContentfulTextNote sectionKey="health_section" />
          <NoEligiblePlans />
        </FadeUp>
      );
    }

    return (
      <>
        <PageLayout page="results">
          {isReviewModeEnabled && tableViewEnabled ? renderResults() : renderResultsInGrid()}
          {device === 'mobile' && availablePlans.length < 5 && (
            <BottomCards>
              {providersSearchBannerCard}
              {predictionsOrClaimsCard}
              {accountsBannerCard}
              {buildPlanBannerCard}
            </BottomCards>
          )}
        </PageLayout>
        {isPlanComparisonEnabled && (
          <CompareBar killMedRec={killMedRec} handleShowCompareModal={compareOnOpen} />
        )}
      </>
    );
  };

  return (
    <>
      <div id="result-page">
        <ContentfulHeaderWrapperForHealthSection
          numAvailablePlans={availablePlans.length}
          hideSubtext={isShowLoading || (selectedProductIsBc && !isReviewModeEnabled)}
          useHeaderAlternateText={selectedProductIsBc}
          animate={isFirstPageView}
          showPreviousSelectedPlanMessage={showPreviousSelectedPlanMessage}
          isReturnUser={!!isReturnUser}
          hidePlanScoringText={killMedRec}
          isReviewModeEnabled={isReviewModeEnabled}
        />
        {availablePlans.length !== 0 && (
          <FadeUp isOpen={isShowResults}>
            <ClientDisclaimer
              hasSpouse={hasSpouse}
              maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution}
            />
          </FadeUp>
        )}
        <AriaHiddenAlert>{hiddenAlert}</AriaHiddenAlert>
        <FadeUp timeout={300} delay={750} isOpen={isShowLoading}>
          <LoadingScreen
            household={household}
            handleOnLoadingComplete={handleOnLoadingComplete}
            isLoading={isLoading}
          />
        </FadeUp>
        {renderPageContent()}
      </div>
      <EducationalDrawer
        handleClose={hsaOnClose}
        isOpen={hsaOpen}
        infoType={SAVINGS_ACCOUNT_TYPES.HSA}
        individualHsaContributionLimit={individualHsaContributionLimit}
        familyHsaContributionLimit={familyHsaContributionLimit}
        employerContribution={employerHSAContributionMax}
        selectedPlan={selectedPlan}
        shouldShowEducationalVideo={!is_educational_videos_enabled || builderGoSettings?.show_hsa_video}
      />
      <EducationalDrawer
        isOpen={hraOpen}
        handleClose={hraOnClose}
        infoType={SAVINGS_ACCOUNT_TYPES.HRA}
        employerContribution={employerHRAContributionMax}
        selectedPlan={selectedPlan}
        shouldShowEducationalVideo={!is_educational_videos_enabled || builderGoSettings?.show_hra_video}
      />
      <EducationalDrawer
        isOpen={fsaOpen}
        handleClose={fsaOnClose}
        infoType={SAVINGS_ACCOUNT_TYPES.FSA}
        employerContribution={0}
        selectedPlan={selectedPlan}
        shouldShowEducationalVideo={!is_educational_videos_enabled || builderGoSettings?.show_fsa_video}
      />
      <ExplainerModal handleClose={predictionsOnClose} isOpen={predictionsOpen} plyData={peopleLikeYou} />
      <PlanEntryModal
        handleClose={planEntryOnClose}
        isOpen={planEntryOpen}
        getRecommendations={(plan) => handleEphemeralPlanRecommendation(plan)}
        plan={enteredPlan}
        savePlan={saveEphemeralPlan}
        individualHsaContributionLimit={individualHsaContributionLimit}
        familyHsaContributionLimit={familyHsaContributionLimit}
        isFamily={isFamily}
        maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution}
      />
      {!isReviewModeEnabled && (
        <CompareModal
          handleClose={compareOnClose}
          isOpen={compareOpen}
          // useLegacyCostCalc is true when is_real_cost_before_tax_benefit_enabled is false.
          // Remove arg when retiring feature flag APP-1028-oop-estimate-uses-comm-api
          selectedPlan={
            hasSelectedRecommendation
              ? new Plan(
                  selectedPlan as RecommendationType,
                  !is_real_cost_before_tax_benefit_enabled, // Remove arg when retiring feature flag APP-1028-oop-estimate-uses-comm-api
                )
              : {}
          }
          choosePlan={handleSelectHealthPlan}
          deselectPlan={handleDeselect}
          hsaContributionLimit={hsaContributionLimit}
          maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution}
          killMedRec={killMedRec}
          showCompareRows
          reviewModePlans={[]}
        />
      )}
    </>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, ResultPageStateProps>({
  selectedProduct: makeGetSelectedProduct(),
  showClientSurvey: makeSelectShowClientSurvey(),
  killMedRec: makeGetBuilderGoSetting('kill_med_rec'),
  isLoading: makeSelectCommercialField('isLoading'),
  household: makeGetHousehold(),
  hasSpouse: makeGetHasSpouse(),
  spouse: makeSelectSpouse(),
  isFamily: makeGetIsFamily(),
  availablePlans: makeGetPlans(),
  maximumAvailableAnnualSihraContribution: makeSelectCommercialField('annual_sihra_contribution'),
  peopleLikeYou: makeSelectCommercialField('people_like_you'),
  hsaContributionLimit: makeGetHsaContributionLimit(),
  individualHsaContributionLimit: makeSelectGlobalField('hsaContributionLimitIndividual'),
  familyHsaContributionLimit: makeSelectGlobalField('hsaContributionLimitFamily'),
  isDirty: makeSelectGlobalField('isDirty'),
  isFullBenefitEnabled: makeSelectConfigField('is_fbs_enabled'),
  canWaiveCoverage: makeSelectConfigField('can_waive_coverage'),
  selectedPlan: makeSelectCommercialField('selectedHealthPlan'),
  isPLYEnabled: makeSelectConfigField('is_ply_enabled'),
  enteredPlan: makeSelectCommercialField('entered_plan'),
  isSpousalPlanEnabled: makeSelectConfigField('is_spousal_plan_comparison_enabled'),
  isPlanComparisonEnabled: makeSelectConfigField('is_plan_comparison_enabled'),
  supplementalPlans: makeSelectOverviewField('supplemental_plans_full_household'),
  isFirstPageView: selectIsFirstPageView(RESULT_PATH),
  searchProviderLinks: makeSelectConfigField('provider_search_links'),
  selectedMemberIds: makeGetHealthPlanMemberIds(),
  survey: makeSelectSurvey(),
  isCoverageWaived: makeSelectCommercialField('isCoverageWaived'),
  jvp: makeSelectGlobalField('jvp'),
  isDemoMode: makeSelectGlobalField('isDemoMode'),
  planYear: makeGetSelectedPublicationPlanYear(),
  validatedSegmentSlug: makeGetSelectedPublicationSegmentSlug(),
  bcUrlHash: makeSelectGlobalField('bcUrlHash'),
  builderCustomerKey: makeSelectConfigField('builder_customer_key'),
  incentiveSurvey: makeSelectProfileField('incentiveSurvey'),
  isIncentiveSurveyLoading: makeSelectProfileField('incentiveSurveyIsLoading'),
  incentiveSurveyAnswers: makeSelectProfileField('incentiveSurveyAnswers'),
  resultPageIncentiveSurvey: makeSelectProfileField('resultPageIncentiveSurvey'),
  isReturnUser: makeSelectJvpField('isReturnUser'),
  elections: makeSelectGlobalField('elections'),
  tierCode: makeGetTierCodeData(),
  hasSelectedRecommendation: makeHasSelectedRecommendation(),
  builderGoSettings: makeGetBuilderGoSettings(),
  claimsSummaryId: makeSelectProfileField('claimsSummaryId'),
  claimsSummary: makeSelectProfileField('claimsSummary'),
  claimsSummaryPolling: makeSelectProfileField('claimsSummaryPolling'),
  claimsError: makeSelectProfileField('claimsError'),
});

export function mapDispatchToProps(dispatch): ResultPageDispatchProps {
  return {
    getRecommendations: () => dispatch(getResultsPageData()),
    resetSelectedSupplementalPlans: () => dispatch(setDefaultSelectedPlans([], {})),
    clearSupplementalCache: () => dispatch(clearSupplementalPlansCache()),
    selectHealthPlan: (plan) => dispatch(selectHealthPlan(plan)),
    selectHealthPlanStoreOnly: (plan) => dispatch(selectHealthPlanStoreOnly(plan)),
    updateActiveMembers: (members) => dispatch(setActiveHouseholdMembers(members)),
    saveEphemeralPlan: (plan) => dispatch(changeFormValue(plan, 'entered_plan')),
    getRecommendationsWithEphemeralPlan: (plan) => dispatch(recommendWithEphemeralPlan(plan)),
    waiveCoverage: () => dispatch(waiveHealthCoverage()),
    fetchSupplementalPlans: () => dispatch(getSupplementalPlans()),
    lockFuturePages: () => dispatch(handleUnlockMainNav(RESULT_PATH)),
    setSelectedMemberIds: (memberIds: string[]) => dispatch(setSelectedMemberIds(memberIds)),
    handleSavePregnancyAnswer: (value: string) =>
      dispatch(changeSurveyValueFromResultPage(value, 'plan_child_question')),
    createUpdateHouseholdAndIncentiveSurvey: () => dispatch(updateHouseholdFromResultPage()),
    submitIncentiveSurveyResponses: (responses: Record<string, IncentiveSurveyAnswer | null>) =>
      dispatch(submitResultIncentiveSurvey(responses)),
    setProfileIncentiveSurvey: (data) => dispatch(setIncentiveSurvey(data)),
    updateElections: (data: Election[] | []) => dispatch(updateElections(data)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(ResultPage);
